import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useSSEEvents } from '@capitalise/common';
import { getEventsAccess } from '.';

let sseEvent;

export const useQueryGetEventStreamAccess = (enabled = true) =>
  useQuery(
    'events-stream-access',
    async () => {
      const result = await getEventsAccess();
      return result?.data?.value;
    },
    {
      enabled,
      retry: false,
      staleTime: 1000 * 60 * 60, // 1 hour
    },
  );

export const useSSELoadingByEntityAndEvent = (
  entityId,
  eventType = 'entity/ledger/profile/update',
  maxTimeoutToComplete = 30000,
) => {
  const startedListening = useRef(false);
  const timeoutTimer = useRef(null);
  const [status, setStatus] = useState({ loading: false, isCompleted: false, isError: false });
  const event = useSSEEvents('event-stream-by-entity');

  useEffect(() => {
    return () => {
      clearTimeout(timeoutTimer.current);
      startedListening.current = false;
    };
  }, []);

  const eventData = event?.data ?? {};
  const isCompleted = !!eventData.finished || parseInt(eventData.percentage, 10) === 100;
  const isSameEntity = parseInt(eventData.entityId, 10) === parseInt(entityId, 10);
  const isSameEvent = event?.event === eventType;
  if (isSameEntity && isSameEvent && isCompleted) {
    setStatus((status) => ({
      ...status,
      loading: false,
      isCompleted: true,
      isError: false,
    }));
  } else if (isSameEntity && !isCompleted && !status.loading) {
    if (!timeoutTimer.current) {
      timeoutTimer.current = setTimeout(() => {
        if (!status.isCompleted) {
          setStatus((status) => ({
            ...status,
            loading: false,
            isCompleted: false,
            isError: true,
          }));
        }
        timeoutTimer.current = null;
      }, maxTimeoutToComplete);
    }
    setStatus((status) => ({
      ...status,
      loading: true,
      isCompleted: false,
      isError: false,
    }));
  }

  return status;
};

export const useSSELoadingMultipleEntities = (
  user,
  eventType,
  eventDataKey,
  entityList,
  finishCallback,
  enableFinish = false,
) => {
  const [entities, setEntities] = useState(entityList);
  const event = useSSEEvents();

  useEffect(() => {
    if (event) {
      if (event.event === eventType && event.data.percentage === 100) {
        if (entities.some((entity) => entity === event.data[eventDataKey])) {
          setEntities((prevEntities) =>
            prevEntities.filter((entity) => entity !== event.data[eventDataKey]),
          );
        }

        if (enableFinish && entities.length === 0) {
          finishCallback();
        }
      }
    }
  }, [event, enableFinish, entities]);
};

export const useSSEEntityEvent = ({ user, eventType, finishCallback, overide = true }) => {
  const event = useSSEEvents();

  if (eventType && event?.event === eventType) {
    finishCallback?.(event);
  }
};
